import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, Rss, Paperclip, ExternalLink, X, Gamepad2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useLocation } from 'react-router-dom';
import { useGroup } from '@/context/GroupContext';
import { useTheme } from '@/context/ThemeContext';

function useSafeGroup() {
  try {
    return useGroup();
  } catch {
    return {
      unreadNewsCount: 0,
      markNewsRead: async () => {},
      markAllVisibleNewsRead: async () => {},
      refreshUnreadNews: async () => {},
    };
  }
}
import { useAuth } from '@/context/AuthContext';
import { config } from '@/config';
import NewsAvatar from '@/components/NewsAvatar';
import GamePlayPanel, { GameHintBlock } from '@/components/games/GamePlayPanel';
import SeasonRankingPanel, { RANKING_VIEW_OPTIONS } from '@/components/games/SeasonRankingPanel';
import { FilterPills } from '@/components/games/GameResultsPanel';
import { GAME_KIND_LABELS, GAME_PLAY_HINTS } from '@/utils/groupGameBareme';

const API = config.BACKEND_URL;

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* ---- Item d'actualité ---- */
const OPEN_GAME_NEWS_KEY = 'prysma_open_game_news';

function NewsItem({ news, onMarkRead, alreadyRead, token, meId }) {
  const isGame = !!(news.game_instance_id || news.is_game || news.is_season_card);
  const [open, setOpen] = useState(() => {
    if (!isGame) return false;
    try { return sessionStorage.getItem(OPEN_GAME_NEWS_KEY) === news.id; } catch { return false; }
  });
  const [seasonFinished, setSeasonFinished] = useState(news.game_phase === 'rankings');
  const [viewTab, setViewTab] = useState('play');
  const [rankingTab, setRankingTab] = useState('live');
  const [rankingFiltersHost, setRankingFiltersHost] = useState(null);

  useEffect(() => {
    setSeasonFinished(news.game_phase === 'rankings');
  }, [news.game_phase, news.game_instance_id]);

  const showPlay = isGame && news.game_phase === 'play' && news.game_instance_id && !seasonFinished;

  useEffect(() => {
    setViewTab(showPlay ? 'play' : 'rankings');
    setRankingTab(news.game_instance_id ? 'live' : 'cumul');
  }, [showPlay, news.id, news.game_instance_id]);

  const trackDetail = useCallback(async () => {
    if (!token) return;
    try { await fetch(`${API}/group/news/${news.id}/mark-detail`, { method: 'POST', headers: authHeaders(token) }); } catch {}
  }, [news.id, token]);

  const trackDownload = useCallback(async () => {
    if (!token) return;
    try { await fetch(`${API}/group/news/${news.id}/mark-download`, { method: 'POST', headers: authHeaders(token) }); } catch {}
  }, [news.id, token]);

  const handleOpen = () => {
    setOpen(true);
    if (isGame) {
      try { sessionStorage.setItem(OPEN_GAME_NEWS_KEY, news.id); } catch { /* ignore */ }
    }
    if (!alreadyRead) onMarkRead(news.id);
    trackDetail();
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!isGame) return;
    try {
      if (next) sessionStorage.setItem(OPEN_GAME_NEWS_KEY, news.id);
      else if (sessionStorage.getItem(OPEN_GAME_NEWS_KEY) === news.id) {
        sessionStorage.removeItem(OPEN_GAME_NEWS_KEY);
      }
    } catch { /* ignore */ }
  };

  return (
    <>
      <div
        className={`relative p-3 rounded-lg border cursor-pointer transition-colors hover:bg-white dark:hover:bg-gray-700/60 ${
          !alreadyRead
            ? 'border-orange-200 dark:border-orange-800 bg-orange-50/40 dark:bg-orange-950/10'
            : 'border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-800/60'
        }`}
        onClick={handleOpen}
      >
        {isGame ? (
          <span className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 rounded-full bg-[#f76f4b] text-white text-[10px] font-bold px-2 py-0.5">
            <Gamepad2 size={10} /> Jeu
          </span>
        ) : (
          <NewsAvatar src={news.avatar_url} size="card" alt="" className="absolute top-2.5 right-2.5" />
        )}
        {!isGame && news.image_url && (
          <img src={news.image_url} alt={news.titre} className="w-full h-24 object-cover rounded-lg mb-2 bg-gray-100" />
        )}
        <div className={`flex items-start gap-1.5 ${news.avatar_url || isGame ? 'pr-16' : ''}`}>
          {!alreadyRead && (
            <span className="flex-shrink-0 w-2 h-2 rounded-full bg-orange-500 mt-1.5 animate-pulse" />
          )}
          <div className="min-w-0">
            <p className="text-xs font-semibold text-gray-900 dark:text-white line-clamp-2 leading-snug">{news.titre}</p>
            {isGame && news.game_kind ? (
              <p className="text-[10px] text-[#f76f4b] mt-0.5">{GAME_KIND_LABELS[news.game_kind] || 'Jeu en cours'}</p>
            ) : null}
            {isGame && !news.game_kind && news.game_phase === 'rankings' ? (
              <p className="text-[10px] text-gray-400 mt-0.5">Classements de la saison</p>
            ) : null}
            <p className="text-[10px] text-gray-400 mt-0.5">{formatDate(news.publie_at)}</p>
          </div>
        </div>
        {!isGame && news.description && (
          <p className={`text-[11px] text-gray-500 dark:text-gray-400 mt-1.5 line-clamp-2 ${news.avatar_url ? 'pr-16' : ''}`}>{news.description}</p>
        )}
        {!isGame && news.fichiers?.length > 0 && (
          <div className="flex items-center gap-1 mt-1.5 text-[10px] text-gray-400">
            <Paperclip size={9} /> {news.fichiers.length} pièce{news.fichiers.length > 1 ? 's' : ''} jointe{news.fichiers.length > 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Modal de détail */}
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          data-game-season-dialog={isGame ? '' : undefined}
          overlayClassName={
            isGame
              ? 'lg:left-64 lg:right-[max(15vw,220px)]'
              : undefined
          }
          className={`max-h-[90vh] overflow-hidden text-left ${
            isGame
              ? 'h-[90vh] flex flex-col pr-12 w-[calc(100vw-1.5rem)] max-w-[calc(100vw-1.5rem)] data-[state=open]:zoom-in-100 data-[state=open]:slide-in-from-left-0 lg:w-auto lg:max-w-none'
              : 'w-[95vw] max-w-2xl'
          }`}
          style={{ zIndex: 10200 }}
        >
          <div className={`min-w-0 ${isGame ? 'flex-1 min-h-0 overflow-y-auto pr-3' : 'max-h-[calc(90vh-3rem)] overflow-y-auto'}`}>
            {isGame ? (
              <>
                <div className="flex items-start justify-between gap-3 min-w-0">
                  <div className="min-w-0">
                    <DialogTitle className="text-base leading-snug break-words whitespace-normal text-left flex items-center gap-2 min-w-0">
                      <Gamepad2 size={16} className="text-[#f76f4b] shrink-0" />
                      <span className="truncate">{news.titre}</span>
                    </DialogTitle>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {showPlay
                        ? (viewTab === 'rankings'
                          ? 'Classements'
                          : (GAME_KIND_LABELS[news.game_kind] ? `À vous de jouer — ${GAME_KIND_LABELS[news.game_kind]}` : 'À vous de jouer'))
                        : 'Classement de la saison'}
                    </p>
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1.5 max-w-[min(100%,24rem)]">
                    {showPlay && viewTab === 'play' ? (
                      <FilterPills
                        value={viewTab}
                        options={[['play', 'Jouer'], ['rankings', 'Classement']]}
                        onChange={(next) => {
                          setViewTab(next);
                          if (next === 'rankings') setRankingTab('live');
                        }}
                        align="end"
                        compact
                        nowrap
                      />
                    ) : (
                      <>
                        <FilterPills
                          value={viewTab === 'play' ? 'play' : rankingTab}
                          options={[
                            ...(showPlay ? [['play', 'Jouer']] : []),
                            ...RANKING_VIEW_OPTIONS,
                          ]}
                          onChange={(next) => {
                            if (next === 'play') setViewTab('play');
                            else {
                              setViewTab('rankings');
                              setRankingTab(next);
                            }
                          }}
                          align="end"
                          compact
                          nowrap
                        />
                        <div ref={setRankingFiltersHost} className="flex flex-col items-end gap-1.5 w-full" />
                      </>
                    )}
                  </div>
                </div>
                {showPlay && viewTab === 'play' ? (
                  <div className="mt-2">
                    <GameHintBlock kind={news.game_kind} />
                  </div>
                ) : null}
                {showPlay ? (
                  <div className={viewTab === 'play' ? '' : 'hidden'}>
                    <GamePlayPanel
                      instanceId={news.game_instance_id}
                      token={token}
                      meId={meId}
                      phase="play"
                      showHint={!GAME_PLAY_HINTS[news.game_kind]}
                      onFinished={() => {
                        setSeasonFinished(true);
                        setViewTab('rankings');
                        setRankingTab('live');
                      }}
                    />
                  </div>
                ) : null}
                {!showPlay || viewTab === 'rankings' ? (
                  <SeasonRankingPanel
                    token={token}
                    meId={meId}
                    compact
                    currentInstanceId={news.game_instance_id || undefined}
                    tab={rankingTab}
                    onTabChange={setRankingTab}
                    hideMainTabs
                    filtersHost={rankingFiltersHost}
                  />
                ) : null}
              </>
            ) : (
              <>
            <NewsAvatar
              src={news.avatar_url}
              size="detail"
              alt=""
              className="float-right [shape-outside:circle(50%)]"
              style={{ shapeMargin: '1.5rem', marginRight: '1.5rem', marginBottom: '1.5rem' }}
            />
            <DialogTitle className="text-base leading-snug pr-6 break-words whitespace-normal text-left">{news.titre}</DialogTitle>
            <p className="text-xs text-gray-400 mt-0.5">{formatDate(news.publie_at)}</p>
            {news.image_url && (
              <img src={news.image_url} alt={news.titre} className="mt-4 w-full rounded-lg object-contain max-h-56 bg-gray-100" />
            )}
            {news.description && (
              <p className="mt-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap break-words">
                {news.description}
              </p>
            )}
            {news.fichiers?.length > 0 && (
              <div className="clear-both pt-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Pièces jointes</h3>
                <div className="space-y-1.5">
                  {news.fichiers.map(f => (
                    <a
                      key={f.id}
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      download={f.nom}
                      onClick={trackDownload}
                      className="flex items-center gap-2 text-xs text-blue-600 hover:underline p-2 bg-gray-50 dark:bg-gray-900 rounded border"
                    >
                      <Paperclip size={11} />
                      <span className="truncate flex-1">{f.nom}</span>
                      <ExternalLink size={10} className="flex-shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ======================================================= */
/*                    NewsSidebar                           */
/* ======================================================= */
export default function NewsSidebar({ hidden = false }) {
  const { token, user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { pathname } = useLocation();
  const { unreadNewsCount, markNewsRead, markAllVisibleNewsRead, refreshUnreadNews } = useSafeGroup();

  const isHomePage = pathname === '/';

  /* ── Données communes ── */
  const [news, setNews] = useState([]);
  const [readIds, setReadIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const markedRef = useRef(false); // évite de rejouer le marquage initial

  /* ── État toggle mobile uniquement ── */
  const [mobileOpen, setMobileOpen] = useState(false);

  const newsRef = useRef(news);
  newsRef.current = news;

  const loadNews = useCallback(async () => {
    if (!token) return;
    let gameModalOpen = false;
    try { gameModalOpen = !!sessionStorage.getItem(OPEN_GAME_NEWS_KEY); } catch { /* ignore */ }
    if (gameModalOpen && newsRef.current.length > 0) {
      await refreshUnreadNews();
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/group/news`, { headers: authHeaders(token) });
      if (res.ok) setNews(await res.json());
      await refreshUnreadNews();
    } finally {
      setLoading(false);
    }
  }, [token, refreshUnreadNews]);

  /* Chargement automatique desktop dès que la page d'accueil est active + rafraîchissement périodique (parution programmée) */
  useEffect(() => {
    if (!isHomePage || !token) return;
    loadNews();
    const intervalId = setInterval(loadNews, 60_000);
    return () => clearInterval(intervalId);
  }, [isHomePage, token, loadNews]);

  /* Marquage "lu" automatique desktop après chargement (une seule fois) */
  // eslint-disable-next-line
  useEffect(() => {
    if (!news.length || markedRef.current) return;
    const unreadIds = news.filter(n => !readIds.has(n.id)).map(n => n.id);
    if (unreadIds.length > 0) {
      markedRef.current = true;
      markAllVisibleNewsRead(unreadIds);
      setReadIds(prev => new Set([...prev, ...unreadIds]));
    }
  }, [news]);

  const handleMarkRead = useCallback(async (newsId) => {
    setReadIds(prev => new Set([...prev, newsId]));
    await markNewsRead(newsId);
  }, [markNewsRead]);

  /* Mobile : ouvrir + charger */
  const handleMobileOpen = useCallback(async () => {
    setMobileOpen(true);
    await loadNews();
    const unreadIds = news.filter(n => !readIds.has(n.id)).map(n => n.id);
    if (unreadIds.length > 0) {
      await markAllVisibleNewsRead(unreadIds);
      setReadIds(prev => new Set([...prev, ...unreadIds]));
    }
  }, [loadNews, markAllVisibleNewsRead, news, readIds]);

  const hasUnread = unreadNewsCount > 0 && !markedRef.current;

  if (!isHomePage) return null;

  const renderNewsList = () => (
    loading && news.length === 0 ? (
      <div className="flex justify-center pt-8"><div className="spinner" /></div>
    ) : news.length === 0 ? (
      <div className="flex flex-col items-center py-10 text-gray-400">
        <Rss size={28} className="mb-2 opacity-30" />
        <p className="text-[11px] text-center">Aucune actualité pour le moment</p>
      </div>
    ) : (
      news.map(n => (
        <NewsItem
          key={n.id}
          news={n}
          alreadyRead={readIds.has(n.id)}
          onMarkRead={handleMarkRead}
          token={token}
          meId={user?.id}
        />
      ))
    )
  );

  return (
    <>
      {/* ════════════════════════════════════════
          MOBILE / TABLETTE — flèche + sidebar toggle
          ════════════════════════════════════════ */}

      {/* Bouton flèche mobile */}
      <div
        className={`lg:hidden fixed top-1/2 z-[200] ${hidden ? 'invisible pointer-events-none' : ''}`}
        style={{
          right: mobileOpen ? 'calc(max(15vw, 220px) + 12px)' : '12px',
          transform: 'translateY(-50%)',
          transition: 'right 0.35s cubic-bezier(0.4,0,0.2,1)',
          width: '32px',
          height: '32px',
        }}
      >
        {hasUnread && (
          <span
            className="absolute inset-0 rounded-full animate-ping"
            style={{ border: '2px solid rgb(249,115,22)', opacity: 0.6 }}
          />
        )}
        <button
          onClick={mobileOpen ? () => setMobileOpen(false) : handleMobileOpen}
          title={mobileOpen ? 'Fermer les actualités' : 'Actualités du groupe'}
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '9999px',
            border: hasUnread
              ? '2px solid rgb(249,115,22)'
              : `1px solid ${isDark ? 'rgb(75,85,99)' : 'rgb(209,213,219)'}`,
            background: isDark ? 'rgb(31,41,55)' : 'white',
            color: hasUnread
              ? 'rgb(249,115,22)'
              : isDark ? 'rgb(156,163,175)' : 'rgb(156,163,175)',
            cursor: 'pointer',
            boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.4)' : '0 1px 3px rgba(0,0,0,0.12)',
          }}
        >
          {mobileOpen ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Panel sidebar mobile */}
      <div
        className={`lg:hidden fixed right-0 top-0 h-full flex flex-col bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-xl z-[199] overflow-hidden ${hidden ? 'invisible pointer-events-none' : ''}`}
        style={{
          width: mobileOpen ? 'max(15vw, 220px)' : 0,
          transition: 'width 0.35s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {mobileOpen && (
          <>
            <div className="flex-shrink-0 px-3 py-2.5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-white dark:bg-gray-800">
              <div className="flex items-center gap-2">
                <Rss size={14} className="text-[#f76f4b]" />
                <span className="text-sm font-bold text-gray-800 dark:text-white">Actualités</span>
              </div>
              <button onClick={() => setMobileOpen(false)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400">
                <X size={14} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              {renderNewsList()}
            </div>
          </>
        )}
      </div>

      {/* ════════════════════════════════════════
          DESKTOP — toujours visible, panel fixe
          ════════════════════════════════════════ */}
      <aside
        className={`hidden lg:flex fixed right-0 top-0 h-full flex-col z-[199] ${hidden ? 'lg:!hidden' : ''}`}
        style={{ width: 'max(15vw, 220px)' }}
      >
        {/* Liseré orange en haut */}
        <div className="h-0.5 w-full bg-gradient-to-r from-orange-400 via-orange-500 to-orange-400 flex-shrink-0" />

        {/* Header */}
        <div className="flex-shrink-0 px-3 py-2.5 flex items-center justify-between bg-white dark:bg-gray-800 border-b border-l border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Rss size={14} className="text-[#f76f4b]" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-gray-600 dark:text-gray-300">
              Actualités
            </span>
          </div>
          {unreadNewsCount > 0 && !markedRef.current && (
            <span className="h-5 min-w-[20px] px-1.5 flex items-center justify-center rounded-full bg-orange-500 text-[10px] font-bold text-white shadow-sm">
              {unreadNewsCount > 9 ? '9+' : unreadNewsCount}
            </span>
          )}
        </div>

        {/* Liste scrollable */}
            <div className="flex-1 overflow-y-auto p-2 space-y-2 bg-gray-50 dark:bg-gray-900/60 border-l border-gray-200 dark:border-gray-700">
              {renderNewsList()}
            </div>
      </aside>
    </>
  );
}
